import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DeleteBasicItemModal from "app/components/AllItems/DeleteBasicItemModal";
import DeleteItemModal from "app/components/AllItems/DeleteItemModal";
import DuplicateItemModal from "app/components/AllItems/DuplicateItemModal";
import FareharbourItemListCard from "app/components/AllItems/FareharbourItemListCard";
import RefereeItemListCard from "app/components/AllItems/RefereeItemListCard";
import NewItemForm from "app/components/NewItemForm/utils/NewItemForm";
import { PageContainer } from "app/components/PageContainer";
import {
  useCreateItemMutation,
  useDeleteItemMutation,
  useGetItemsByBusinessQuery,
  useUpadateBasicItemMutation,
} from "lib/features/item/itemService";
import { Item, UpdateBasicItemDto } from "lib/features/item/types";
import {
  selectCustomerDetails,
  selectIsExistingBookingLink,
} from "lib/features/message/messageSlice";
import { getBusinessIdAsNumberFn } from "lib/hooks/queries/useGetCurrentBusiness";
import { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import itemServices from "services/itemService";
import { availabilityScreenStyles } from "styles/AvailabilityDirectory/availabilityScreen.styles";
import { formatCustomerName } from "util/formatCustomerName";

export default function AllItems() {
  const navigate = useNavigate();

  const customerDetails = useSelector(selectCustomerDetails);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);

  const [businessId, setBusinessId] = useState<number | null>(null);
  const [item, setItem] = useState<Item | null>(null);
  const [itemName, setItemName] = useState<string>("");
  const [items, setItems] = useState<Item[]>([]);

  const [isNewFormOpen, setIsNewFormOpen] = useState<boolean>(false); //testing
  const [visibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [visibleDuplicateModal, setIsVisibleDuplicateModal] =
    useState<boolean>(false);
  const [visibleBasicModal, setIsVisibleBasicModal] = useState<boolean>(false);
  const [isBusinessIdLoading, setIsBusinessIdLoading] = useState<boolean>(true);

  const {
    data,
    isLoading: itemsLoading,
    refetch,
  } = useGetItemsByBusinessQuery(businessId!, {
    skip: !businessId, // Prevents running if businessId is null,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  });

  const [deleteItem, { isLoading: deleteLoading }] = useDeleteItemMutation();
  const [createItem, { isLoading: createLoading }] = useCreateItemMutation();
  const [upadateBasicItem, { isLoading: editLoading }] =
    useUpadateBasicItemMutation();

  const fetchBusinessId = async () => {
    try {
      const id = await getBusinessIdAsNumberFn();
      setBusinessId(id);
    } catch (error) {
      console.log(error);
    } finally {
      setIsBusinessIdLoading(false);
    }
  };

  const addItem = async () => {
    setIsNewFormOpen(true);
  };

  const openDeleteItemModal = (item: Item): void => {
    setItem(item);
    setIsVisibleDeleteModal(true);
  };

  const openDuplicateItemModal = (item: Item): void => {
    setItem(item);
    setIsVisibleDuplicateModal(true);
  };

  const openBasicItemEditModal = async (item: Item) => {
    setItem(item);
    setIsVisibleBasicModal(true);
  };

  // This code will change the fareharbour record_status
  const onHideAndShowFareharbourItem = async () => {
    if (!item) return;
    try {
      await itemServices.hideItem(item.id, !item.hide_item);
      setIsVisibleBasicModal(false);
      setItems((prevItems) =>
        prevItems.map((i) =>
          i.id === item.id ? { ...i, hide_item: !item.hide_item } : i,
        ),
      );
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const onDeleteRefereeItem = async () => {
    if (!item) return;
    try {
      await deleteItem(item.id).unwrap();
      setIsVisibleDeleteModal(false);
      setItems((prevItems) => prevItems.filter((i) => i.id !== item.id));
    } catch (error) {
      console.log("error: ", error);
    }
  };
  const hideItem = async (item: Item) => {
    setItem(item);
    setIsVisibleBasicModal(true);
  };

  const onDuplicate = async () => {
    if (!item) return;

    try {
      let body = {
        businessId: item?.business?.id,
        item_type: item?.item_type,
        item_name: itemName,
        headline: item?.itemDetails[0]?.headline,
        description: item?.itemDetails[0]?.description,
        whats_included: item?.itemDetails[0]?.whats_included,
        cancellation_policy: item?.itemDetails[0]?.cancellation_policy,
        unit_name: item?.itemDetails[0]?.unit_name,
        price: item?.itemDetails[0]?.price,
        capacity: item?.itemDetails[0]?.capacity,
        price_description: item?.itemDetails[0]?.price_description,
        comission: item?.itemDetails[0]?.comission,
        additional_info: item?.itemDetails[0]?.additional_info,
        offering: item?.itemDetails[0]?.offering,
        min_age: item?.itemDetails[0]?.min_age ?? null,
        max_age: item?.itemDetails[0]?.max_age ?? null,
        max_guest: item?.itemDetails[0]?.max_guest ?? null,
        images: item?.itemImages?.map((image) => image.uri) ?? [],
        industry_ids: item?.industries?.map((industry) => industry.id) || [],
        category_ids: item?.categories?.map((category) => category.id) || [],
        additionalOptions: item?.additionalOptions || [],
        selling_points: item?.selling_points ?? "",
        duration_applicable:
          item?.itemDetails[0]?.duration_applicable &&
          item?.itemDetails[0]?.duration
            ? true
            : false,
        duration: item?.itemDetails[0]?.duration ?? undefined,
      };

      const newItem = await createItem(body).unwrap();
      setIsVisibleDuplicateModal(false);
      setItems((prevItem) => [...prevItem, newItem]);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchBusinessId();
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (businessId) {
      refetch();
    }
  }, [businessId]);

  useLayoutEffect(() => {
    if (data) {
      setItems(data);
    }
  }, [data]);
  useEffect(() => {
    console.log("Items updated:", items);
  }, [isNewFormOpen, items]);

  //error handling of empty array

  const refereeItems =
    items?.filter((i: Item) => i.item_type === 1 || i.item_type === 3) || [];
  const integratedItems = items?.filter((i: Item) => i.item_type === 4) || [];

  return (
    <PageContainer>
      {itemsLoading || isBusinessIdLoading || (!!businessId && !data) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNewFormOpen ? (
        <NewItemForm
          setIsNewFormOpen={setIsNewFormOpen}
          businessId={businessId}
        />
      ) : (
        <>
          {customerDetails?.customer_name && isExistingBookingLink && (
            <Box>
              <Typography sx={availabilityScreenStyles.customerDetailsText}>
                Select an Item to create the availability you would like to
                share with{" "}
                <strong>
                  {formatCustomerName(customerDetails?.customer_name)}
                </strong>
              </Typography>
            </Box>
          )}
          {/* remove old condition      {!itemsLoading && !isNewFormOpen && items && items?.length > 0 && (
           */}
          <Box sx={{ marginTop: "2rem", marginX: "10px" }}>
            {refereeItems.length === 0 && integratedItems.length === 0 ? (
              <Box
                sx={{
                  width: "641px",
                  height: "141px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "34px",
                    fontWeight: "700",
                    lineHeight: "46px",
                  }}
                >
                  Ready To Start Earning?
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: "15px",
                    fontWeight: "400",
                    lineHeight: "22.5px",
                    fontFamily: "Poppins",
                  }}
                >
                  Add your first item or import your FareHarbor experiences to
                  activate bookings.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "14px",
                    width: "423px",
                    height: "44px",
                  }}
                >
                  <Button
                    sx={{
                      width: "190px",
                      height: "44px",
                      backgroundColor: "#328734",
                      borderRadius: "8px",
                      p: "12px 20px",
                      fontSize: "17px",
                      fontWeight: "600",
                      lineHeight: "22.5px",
                      fontFamily: "Poppins",
                      "&:hover": {
                        backgroundColor: "#328734",
                      },
                      color: "#fff",
                    }}
                    onClick={() => {
                      addItem();
                    }}
                  >
                    Create New Item
                  </Button>
                  <Button
                    sx={{
                      width: "219px",
                      height: "44px",
                      borderRadius: "8px",
                      border: "1px solid #000",
                      p: "12px 20px",
                      fontSize: "17px",
                      fontWeight: "600",
                      lineHeight: "22.5px",
                      fontFamily: "Poppins",
                      color: "#000",
                    }}
                    onClick={() => {
                      navigate("/settings/integration");
                    }}
                  >
                    Integrate Calendar
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                {/* Referee Items */}

                <Box sx={{ marginBottom: "50px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  ></Box>
                  {refereeItems.length > 0 ? (
                    <Box>
                      <Box
                        sx={{
                          maxWidth: "1300px",
                          height: "58px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mx: "auto",
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: "34px",
                            fontWeight: "700",
                            lineHeight: "46px",
                          }}
                        >
                          Referee Items
                        </Typography>
                        <Button
                          sx={{
                            width: "189px",
                            height: "58px",
                            py: "16px",
                            px: "0",
                            fontSize: "20px",
                            fontWeight: 500,
                            lineHeight: "26px",
                            fontFamily: "Poppins",
                          }}
                          onClick={() => {
                            addItem();
                          }}
                        >
                          + Create New Item
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "24px",
                          mx: "auto",
                          maxWidth: "1300px",
                        }}
                      >
                        {refereeItems.map((item: Item, index: number) => (
                          <RefereeItemListCard
                            key={index}
                            item={item}
                            pressDelete={openDeleteItemModal}
                            pressDuplicte={openDuplicateItemModal}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    // added ui if no item found
                    <Box
                      sx={{
                        mx: "auto",
                        width: "641px",
                        height: "141px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: "34px",
                          fontWeight: "700",
                          lineHeight: "46px",
                        }}
                      >
                        Referee Items
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: "15px",
                          fontWeight: "400",
                          lineHeight: "22.5px",
                          fontFamily: "Poppins",
                        }}
                      >
                        Add your first item
                      </Typography>
                      <Box>
                        <Button
                          sx={{
                            width: "190px",
                            height: "44px",
                            backgroundColor: "#328734",
                            borderRadius: "8px",
                            p: "12px 20px",
                            fontSize: "17px",
                            fontWeight: "600",
                            lineHeight: "22.5px",
                            fontFamily: "Poppins",
                            "&:hover": {
                              backgroundColor: "#328734",
                            },
                            color: "#fff",
                          }}
                          onClick={() => {
                            addItem();
                          }}
                        >
                          Create New Item
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Integrated Items */}
                <Box sx={{ marginBottom: "50px" }}>
                  {items &&
                  !isExistingBookingLink &&
                  integratedItems?.length > 0 ? (
                    <Box
                      sx={{
                        maxWidth: "1300px",
                        mx: "auto",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: "34px",
                          fontWeight: "700",
                          lineHeight: "46px",
                        }}
                      >
                        Integrated Items
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "24px",
                        }}
                      >
                        {integratedItems
                          ?.sort((a, b: Item) =>
                            (b.commission ?? 0) > 0 ? 1 : -1,
                          ) // live item first
                          ?.map((item: Item) => (
                            <FareharbourItemListCard
                              key={item.id}
                              item={item}
                              pressDelete={openBasicItemEditModal}
                              pressHideShow={hideItem}
                            />
                          ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mx: "auto",
                        width: "641px",
                        height: "141px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: "34px",
                          fontWeight: "700",
                          lineHeight: "46px",
                        }}
                      >
                        Integrated Items
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: "15px",
                          fontWeight: "400",
                          lineHeight: "22.5px",
                          fontFamily: "Poppins",
                        }}
                      >
                        Import your FareHarbor experiences to use one calendar
                      </Typography>
                      <Box>
                        <Button
                          sx={{
                            width: "219px",
                            height: "44px",
                            borderRadius: "8px",
                            border: "1px solid #000",
                            p: "12px 20px",
                            fontSize: "17px",
                            fontWeight: "600",
                            lineHeight: "22.5px",
                            fontFamily: "Poppins",
                            color: "#000",
                          }}
                          onClick={() => {
                            navigate("/settings/integration");
                          }}
                        >
                          Integrate Calendar
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}

      <DeleteItemModal
        open={visibleDeleteModal}
        close={() => setIsVisibleDeleteModal(false)}
        loading={deleteLoading}
        onSubmit={onDeleteRefereeItem}
      />

      <DeleteBasicItemModal
        item={item}
        open={visibleBasicModal}
        close={() => setIsVisibleBasicModal(false)}
        loading={editLoading}
        onSubmit={onHideAndShowFareharbourItem}
      />

      <DuplicateItemModal
        open={visibleDuplicateModal}
        close={() => setIsVisibleDuplicateModal(false)}
        loading={createLoading}
        onSubmit={onDuplicate}
        setItemName={setItemName}
      />
    </PageContainer>
  );
}
